import definitions from './videoOverVideo.js';
import { getMobilePageHeight } from './mobilePageHeights.js';

function setVideoPlayback(video, shouldPlay) {
  if (!video) return;
  if (shouldPlay) video.play().catch(() => {});
  else video.pause();
}

const videoOverVideoModule = {
  scenes: [],

  init(config = definitions) {
    const container = document.querySelector('.pages-container');
    if (!container) return;
    this.scenes = [];
    for (const definition of config) {
      const page = Array.from(container.querySelectorAll('.pages-inner > .page'))
        .find(element => element.dataset.id === String(definition.page));
      if (!page || !definition.cards?.length || !definition.backgroundVideo?.video) continue;
      const stage = document.createElement('section');
      stage.className = 'video-over-video-stage';
      stage.setAttribute('aria-label', definition.label || 'Featured videos');
      const backgroundVideo = this.createVideo(definition.backgroundVideo, 'video-over-video-background');
      const shade = document.createElement('div');
      shade.className = 'video-over-video-shade';
      const cardTrack = document.createElement('div');
      cardTrack.className = 'video-over-video-cards';
      for (const item of definition.cards) {
        const card = document.createElement('article');
        card.className = 'video-over-video-card';
        card.append(this.createVideo(item, 'video-over-video-card-video'));
        cardTrack.append(card);
      }
      stage.append(backgroundVideo, shade, cardTrack);
      page.classList.add('video-over-video-page');
      page.append(stage);
      this.scenes.push({ definition, page, stage, backgroundVideo, cardTrack, travel: 0 });
    }
    this.container = container;
    this.desktop = matchMedia('(min-width: 768px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.layout();
    container.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.layout);
    this.desktop.addEventListener('change', this.layout);
    this.reducedMotion.addEventListener('change', this.update);
    for (const scene of this.scenes) {
      for (const video of scene.stage.querySelectorAll('video')) {
        video.addEventListener('loadedmetadata', this.layout, { once: true });
      }
    }
  },

  createVideo(definition, className) {
    const video = document.createElement('video');
    video.className = className;
    video.muted = true;
    video.loop = true;
    video.autoplay = false;
    video.playsInline = true;
    video.preload = 'none';
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    if (definition.poster) video.poster = definition.poster;
    if (definition.objectPosition) video.style.objectPosition = definition.objectPosition;
    video.src = definition.video;
    return video;
  },

  layout: () => {
    const module = videoOverVideoModule;
    const { container, desktop, scenes } = module;
    if (!container || !desktop) return;
    const horizontal = desktop.matches;
    const viewportWidth = container.clientWidth;
    const viewportHeight = container.clientHeight;
    for (const scene of scenes) {
      const { page, stage, cardTrack } = scene;
      const pageHeight = horizontal ? viewportHeight : getMobilePageHeight(page.dataset.id, viewportHeight);
      page.style.height = `${pageHeight}px`;
      page.style.setProperty('--video-over-video-width', `${viewportWidth}px`);
      page.style.setProperty('--video-over-video-height', `${viewportHeight}px`);
      cardTrack.style.setProperty('--video-over-video-card-count', String(cardTrack.children.length));
      const cardTravel = Math.max(0, cardTrack.scrollWidth - viewportWidth);
      const configuredSpeed = Number(scene.definition.scrollSpeed);
      const scrollSpeed = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 1;
      const travel = cardTravel / scrollSpeed;
      scene.cardTravel = cardTravel;
      scene.travel = travel;
      page.style.width = horizontal ? `${viewportWidth + travel}px` : `${viewportWidth}px`;
      page.style.height = horizontal ? `${viewportHeight}px` : `${pageHeight + travel}px`;
      page.style.setProperty('--video-over-video-travel', `${travel}px`);
    }
    module.update();
  },

  getScenes() {
    return this.scenes.map(({ page, travel }) => ({
      page,
      travel,
    }));
  },

  update: () => {
    const module = videoOverVideoModule;
    if (!module.container || !module.desktop) return;
    const horizontal = module.desktop.matches;
    const viewport = module.container.getBoundingClientRect();
    for (const { page, stage, backgroundVideo, cardTrack, travel, cardTravel } of module.scenes) {
      const bounds = page.getBoundingClientRect();
      const consumed = horizontal ? viewport.left - bounds.left : viewport.top - bounds.top;
      const progress = Math.max(0, Math.min(1, consumed / Math.max(1, travel)));
      const offset = module.reducedMotion.matches ? 0 : progress * cardTravel;
      cardTrack.style.transform = `translateX(${-offset}px)`;
      page.classList.toggle('video-over-video-page--active', consumed >= 0 && consumed <= travel);
      if (backgroundVideo.paused) backgroundVideo.play().catch(() => {});
      const cards = Array.from(cardTrack.children);
      const stageBounds = stage.getBoundingClientRect();
      cards.forEach(card => {
        const video = card.querySelector('video');
        if (!video) return;
        const bounds = card.getBoundingClientRect();
        const visible = bounds.right > stageBounds.left &&
          bounds.left < stageBounds.right &&
          bounds.bottom > stageBounds.top &&
          bounds.top < stageBounds.bottom;
        setVideoPlayback(video, visible);
      });
      stage.dataset.progress = String(progress);
    }
  },
};

export default videoOverVideoModule;